import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faTimes } from '@fortawesome/free-solid-svg-icons';

const FuelCardQuestionnaireModal = ({
  show,
  onClose,
  onSubmit,
  prefillEmail = '',
  prefillFleetSize = '',
  lead,
}) => {
  // --- Sales Script Data
  const scriptSections = [
    {
      id: 'introduction',
      title: 'Introduction',
      content: `Hi, my name is [Your Name] from FuelLink — do you have a few minutes to chat?
I wanted to take a moment to give you some information regarding our fuel program and how we help fleets like yours save on fuel costs.`,
    },
    {
      id: 'benefits',
      title: 'Benefits',
      content: `1. Exclusive Discounts:
   On average, our customers save $0.42 per gallon, with discounts available at over 4,500 fueling locations nationwide.
2. Custom-Branded Portal:
   A personalized portal branded with your company’s logo and colors helps track transactions, monitor payments, and generate reports.
   - Fraud protection & real-time notifications.
3. Fuel Location Finder:
   Discover the best discounts available before you fuel up.
4. Extensive Network:
   Access all major truck stops and thousands of independent fueling locations.
5. Discount Sharing:
   Share discount information with your drivers.`,
    },
    {
      id: 'engage',
      title: 'Engage',
      content: `"Does that sound like something that could be helpful for your fleet?"`,
    },
    {
      id: 'objections',
      title: 'Handle Objections',
      content: '',
    },
    {
      id: 'questions',
      title: 'Ask Questions',
      content: `- How are you currently managing fuel costs?
- Do you typically fuel at specific locations or go with whatever’s nearby?
- How large is your fleet and what’s your monthly fuel volume?
- Are you happy with your current fuel card or is there something you wish worked better?`,
    },
    {
      id: 'closing',
      title: 'Closing',
      content: `"With everything I’ve mentioned—exclusive discounts, a custom-branded portal, and access to the largest fueling network—I’d love to help you save more on fuel.
Would you like to move forward with a quick transaction analysis or schedule a demo?"`,
    },
  ];

  // --- Objection sections when "Handle Objections" is selected
  const objectionSections = [
    {
      id: 'prepay',
      title: 'Pre-Pay Accounts',
      content: `"I completely understand—pre-paying can feel like a change, but it’s what allows us to offer deeper discounts. We also have options that provide credit lines if needed."`,
    },
    {
      id: 'haveCard',
      title: 'Already have a fuel card',
      content: `"That’s great! Many customers switched to FuelLink after a comparison. Would you be open to letting us compare your current program?"`,
    },
    {
      id: 'discounts',
      title: "Discounts aren't clear",
      content: `"We can review your last 30 days of transactions to show your potential savings."`,
    },
    {
      id: 'fleetSmall',
      title: 'Fleet too small',
      content: `"We work with fleets of all sizes—from a single truck to hundreds. Every dollar saved matters."`,
    },
    {
      id: 'happyProvider',
      title: "Happy with current provider",
      content: `"What’s one thing about your current provider you wish could be improved? FuelLink might be able to match or exceed that experience."`,
    },
    {
      id: 'noTime',
      title: "No time to switch",
      content: `"Our setup is fast and hassle-free—we handle everything, including card distribution and portal setup."`,
    },
    {
      id: 'portalValue',
      title: "No value in a portal",
      content: `"Our portal tracks every transaction and provides real-time fraud alerts, saving you hours of manual work."`,
    },
    {
      id: 'independent',
      title: "Don't fuel at major truck stops",
      content: `"Our network includes thousands of independent fueling locations to ensure you get the best discounts."`,
    },
  ];

  const [selectedSectionId, setSelectedSectionId] = useState(scriptSections[0].id);
  const [selectedObjectionId, setSelectedObjectionId] = useState(objectionSections[0].id);
  const currentScriptSection = scriptSections.find(
    (sec) => sec.id === selectedSectionId
  );

  // --- Questionnaire state; note prefillEmail and prefillFleetSize are used here
  const [answers, setAnswers] = useState({
    hasFuelCards: 'no',
    fuelCardType: '',
    canSendInfo: 'yes',
    bestEmail: prefillEmail,
    preferredFuelLocations: [],
    avgDiscount: '',
    happyWithCurrentCards: 'yes',
    monthlyVolume: '',
    fleetSize: prefillFleetSize,
    areasRun: [],
  });

  // --- Prepopulate form if lead has Questionnaire data
  useEffect(() => {
    if (lead && lead.Questionnaire) {
      const lines = lead.Questionnaire.split('\n');
      const parsed = {};
      lines.forEach((line) => {
        const [key, ...rest] = line.split(':');
        if (key && rest) {
          parsed[key.trim()] = rest.join(':').trim();
        }
      });
      setAnswers((prev) => ({
        ...prev,
        hasFuelCards: parsed.hasFuelCards || prev.hasFuelCards,
        fuelCardType: parsed.fuelCardType || prev.fuelCardType,
        preferredFuelLocations: parsed.preferredFuelLocations
          ? parsed.preferredFuelLocations.split(';').map((s) => s.trim())
          : prev.preferredFuelLocations,
        avgDiscount: parsed.avgDiscount || prev.avgDiscount,
        happyWithCurrentCards: parsed.happyWithCurrentCards || prev.happyWithCurrentCards,
        monthlyVolume: parsed.monthlyVolume || prev.monthlyVolume,
        areasRun: parsed.areasRun
          ? parsed.areasRun.split(';').map((s) => s.trim())
          : prev.areasRun,
      }));
    }
  }, [lead]);

  // --- Options arrays
  const fuelCardOptions = [
    'AtoB',
    'CFN',
    'Comdata Card',
    'ES Advantage',
    'England',
    'Fleet Card',
    'FuelMan Deep Saver',
    'GroundTeq',
    'Loves',
    'Mud Flap',
    'NASTC',
    'P-Fleet',
    'Pilot/Flying J -Axle Fuel Card',
    'RTS Fuel Card',
    'Ramp',
    'Relay',
    'Schneider',
    'TCS',
    'Truckers Advantagae',
    'WEX',
    'WEX- Fleet One',
    'Other',
  ];

  const locationOptions = [
    'Independents/Other',
    'Pilot / Flying J',
    'Loves',
    'TA Petro',
    'Sapp Bros',
    'Wilco',
    'Speedway',
    'Quick Trip',
    'Kangaroo Pantry',
    'Petro Canada',
    'Imperial Oil',
    'Kwik Trip',
    'Quick Fuel',
    'Ambest',
    'CFN',
    'Quarels',
    'Maverik',
    'Road Ranger',
    'SC Fuels',
    'Circle K',
    'RaceTrac',
    "Casey's",
    "AllsupsYesway",
    "GPM",
    "Doesn't Have Preference - Any",
  ];

  const volumeOptions = [
    'Less than 1000 gallons',
    '1k - 5k gallons',
    '5k - 10k',
    'More than 10k',
  ];

  const areaOptions = [
    'Northeast',
    'Southeast',
    'Midwest',
    'Southwest',
    'West',
    'Pacific Northwest',
    'Canada',
    'OTR',
  ];

  // --- Toggle for multi-select fields
  const handleMultiSelect = (field, value) => {
    setAnswers((prev) => {
      const currentValues = prev[field] || [];
      return currentValues.includes(value)
        ? { ...prev, [field]: currentValues.filter((item) => item !== value) }
        : { ...prev, [field]: [...currentValues, value] };
    });
  };

  // --- Generic change handler
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === 'checkbox' ? checked : value;
    setAnswers((prev) => ({ ...prev, [name]: finalValue }));
  };

  // --- Save (Submit) logic
  const handleSave = () => {
    const combinedAnswers = {
      ...answers,
      preferredFuelLocations: answers.preferredFuelLocations.join(';'),
      areasRun: answers.areasRun.join(';'),
    };
    onSubmit(combinedAnswers);
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-30 bg-black bg-opacity-50 p-4">
      {/* Outer container */}
      <div className="bg-white rounded-lg shadow-md w-full max-w-6xl mx-auto flex max-h-[90vh]">
        {/* LEFT COLUMN: SALES SCRIPT */}
        <div className="w-1/4 p-4 border-r flex flex-col">
          <h2 className="text-lg font-semibold mb-3">Sales Script</h2>
          <select
            className="p-2 border rounded mb-4"
            value={selectedSectionId}
            onChange={(e) => setSelectedSectionId(e.target.value)}
          >
            {scriptSections.map((sec) => (
              <option key={sec.id} value={sec.id}>
                {sec.title}
              </option>
            ))}
          </select>
          {selectedSectionId === 'objections' ? (
            <>
              <select
                className="p-2 border rounded mb-4"
                value={selectedObjectionId}
                onChange={(e) => setSelectedObjectionId(e.target.value)}
              >
                {objectionSections.map((obj) => (
                  <option key={obj.id} value={obj.id}>
                    {obj.title}
                  </option>
                ))}
              </select>
              <div className="overflow-y-auto flex-grow border p-2 rounded">
                <pre className="whitespace-pre-wrap text-sm">
                  {objectionSections.find((obj) => obj.id === selectedObjectionId)?.content}
                </pre>
              </div>
            </>
          ) : (
            <div className="overflow-y-auto flex-grow border p-2 rounded">
              <pre className="whitespace-pre-wrap text-sm">
                {currentScriptSection?.content}
              </pre>
            </div>
          )}
        </div>

        {/* CENTER COLUMN: QUESTIONNAIRE */}
        <div className="w-1/2 flex flex-col">
          {/* HEADER */}
          <div className="p-4 border-b flex justify-between">
            <h2 className="text-xl">Fuel Card Questionnaire</h2>
            <FontAwesomeIcon
              icon={faTimes}
              className="text-gray-600 cursor-pointer"
              onClick={onClose}
            />
          </div>
          {/* CONTENT */}
          <div className="p-4 overflow-y-auto flex-grow space-y-4">
            {/* Q1: Fuel Cards? */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                1. Do you have fuel cards currently?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    name="hasFuelCards"
                    value="yes"
                    checked={answers.hasFuelCards === 'yes'}
                    onChange={handleChange}
                  />
                  <span>Yes</span>
                </label>
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    name="hasFuelCards"
                    value="no"
                    checked={answers.hasFuelCards === 'no'}
                    onChange={handleChange}
                  />
                  <span>No</span>
                </label>
              </div>
            </div>
            {/* If Yes, Fuel Card Type */}
            {answers.hasFuelCards === 'yes' && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  What type of Fuel Card?
                </label>
                <select
                  name="fuelCardType"
                  className="mt-1 p-2 block w-full border rounded-md"
                  value={answers.fuelCardType}
                  onChange={handleChange}
                >
                  <option value="">Select Fuel Card...</option>
                  {fuelCardOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Q2: Can we send info */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                2. Can we send you more info about our fuel card program via email?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    name="canSendInfo"
                    value="yes"
                    checked={answers.canSendInfo === 'yes'}
                    onChange={handleChange}
                  />
                  <span>Yes</span>
                </label>
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    name="canSendInfo"
                    value="no"
                    checked={answers.canSendInfo === 'no'}
                    onChange={handleChange}
                  />
                  <span>No</span>
                </label>
              </div>
            </div>
            {/* Q3: Best Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                3. What is the best email for you?
              </label>
              <input
                type="email"
                name="bestEmail"
                className="mt-1 p-2 block w-full border rounded-md"
                value={answers.bestEmail}
                onChange={handleChange}
                placeholder="Enter email..."
              />
            </div>
            {/* Q4: Preferred Fuel Locations */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                4. What fuel locations do you prefer? (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                {locationOptions.map((loc) => (
                  <label key={loc} className="inline-flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={answers.preferredFuelLocations.includes(loc)}
                      onChange={() => handleMultiSelect('preferredFuelLocations', loc)}
                    />
                    <span>{loc}</span>
                  </label>
                ))}
              </div>
            </div>
            {/* Q5: Average Discount */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                5. What would you say your average discount today is?
              </label>
              <input
                type="text"
                name="avgDiscount"
                className="mt-1 p-2 block w-full border rounded-md"
                value={answers.avgDiscount}
                onChange={handleChange}
                placeholder="e.g. $0.10 off per gallon"
              />
            </div>
            {/* Q6: Happy with Current Cards */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                6. Are you happy with your current fuel cards?
              </label>
              <div className="flex space-x-4">
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    name="happyWithCurrentCards"
                    value="yes"
                    checked={answers.happyWithCurrentCards === 'yes'}
                    onChange={handleChange}
                  />
                  <span>Yes</span>
                </label>
                <label className="inline-flex items-center space-x-2">
                  <input
                    type="radio"
                    name="happyWithCurrentCards"
                    value="no"
                    checked={answers.happyWithCurrentCards === 'no'}
                    onChange={handleChange}
                  />
                  <span>No</span>
                </label>
              </div>
            </div>
            {/* Q7: Monthly Volume */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                7. What is your monthly volume?
              </label>
              <select
                name="monthlyVolume"
                className="mt-1 p-2 block w-full border rounded-md"
                value={answers.monthlyVolume}
                onChange={handleChange}
              >
                <option value="">Select volume range...</option>
                {volumeOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            {/* Q8: Fleet Size */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                8. What is your fleet size?
              </label>
              <input
                type="number"
                name="fleetSize"
                className="mt-1 p-2 block w-full border rounded-md"
                value={answers.fleetSize}
                onChange={handleChange}
                placeholder="Number of trucks..."
              />
            </div>
            {/* Q9: Areas Run */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                9. What areas do you run? (Select all that apply)
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                {areaOptions.map((area) => (
                  <label key={area} className="inline-flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={answers.areasRun.includes(area)}
                      onChange={() => handleMultiSelect('areasRun', area)}
                    />
                    <span>{area}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* FOOTER */}
          <div className="p-4 border-t flex justify-end space-x-4 items-center">
            <button onClick={onClose} className="bg-red-500 text-white p-2 rounded">
              Close
            </button>
            <button onClick={handleSave} className="themed-button">
              Submit
            </button>
          </div>
        </div>

        {/* RIGHT COLUMN: LEAD DETAILS */}
        <div className="w-1/4 p-4 border-l overflow-y-auto">
          <h2 className="text-lg font-semibold mb-3">Lead Details</h2>
          {lead && (
            <>
              <p>
                <strong>Contact (Primary):</strong> {lead.CONTACT1 || 'N/A'}
              </p>
              {lead.CONTACT2 && (
                <p>
                  <strong>Contact (Secondary):</strong> {lead.CONTACT2}
                </p>
              )}
              <p>
                <strong>Company (Legal Name):</strong> {lead.LEGALNAME || 'N/A'}
              </p>
              {lead.DBANAME && (
                <p>
                  <strong>DBA Name:</strong> {lead.DBANAME}
                </p>
              )}
              <p>
                <strong>Address:</strong> {lead.ADDRESS || 'N/A'}
              </p>
              <p>
                <strong>City, State, ZIP:</strong>{' '}
                {`${lead.CITY || 'N/A'}, ${lead.STATE || 'N/A'} ${lead.ZIP || ''}`}
              </p>
              <p>
                <strong>Phone:</strong> {lead.PHONE || 'N/A'}
              </p>
              {lead.CELL && (
                <p>
                  <strong>Cell:</strong> {lead.CELL}
                </p>
              )}
              {lead.FAX && (
                <p>
                  <strong>Fax:</strong> {lead.FAX}
                </p>
              )}
              <p>
                <strong>Email:</strong> {lead.EMAIL || 'N/A'}
              </p>
              <p>
                <strong>USDOT:</strong> {lead.USDOT || 'N/A'}
              </p>
              <p>
                <strong>Docket:</strong> {lead.DOCKET || 'N/A'}
              </p>
              <p>
                <strong>Trucks:</strong> {lead.TRUCKS || 'N/A'}
              </p>
              <p>
                <strong>Vehicle Inspections:</strong> {lead.VEHINSP || 'N/A'}
              </p>
              <p>
                <strong>Lead Status:</strong> {lead.LeadStatus || 'N/A'}
              </p>
              <p>
                <strong>Opt Out:</strong> {lead.OptOut ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Mailing List:</strong> {lead.AddedToMailingList ? 'Yes' : 'No'}
              </p>
              <p>
                <strong>Notes:</strong> {lead.Notes || 'N/A'}
              </p>
              <p>
                <strong>Created On:</strong> {lead.CreatedOn || 'N/A'}
              </p>
              <p>
                <strong>Modified On:</strong> {lead.ModifiedOn || 'N/A'}
              </p>
              {lead.SalesRep && (
                <>
                  <hr className="my-2" />
                  <p>
                    <strong>Sales Rep:</strong> {lead.SalesRep.FirstName} {lead.SalesRep.LastName}
                  </p>
                  <p>
                    <strong>Rep Email:</strong> {lead.SalesRep.Email}
                  </p>
                  <p>
                    <strong>Rep Phone:</strong> {lead.SalesRep.Phone}
                  </p>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FuelCardQuestionnaireModal;
